import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft,
  FileText,
  Image,
  Save,
  Trash2,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  CalendarClock,
  Tag,
  RefreshCw,
  Table2,
} from 'lucide-react';
import KeyDataSection from '../components/KeyDataSection';

const CATEGORIES = [
  { id: 'financieel', name: 'Financieel & Belasting' },
  { id: 'contracten', name: 'Contracten & Huur' },
  { id: 'leveranciers', name: 'Leveranciers & Inkoop' },
  { id: 'techniek', name: 'Techniek & Apparatuur' },
  { id: 'voertuigen', name: 'Voertuigen' },
  { id: 'vastgoed', name: 'Vastgoed & Facilitair' },
  { id: 'veiligheid', name: 'Veiligheid & Compliance' },
  { id: 'milieu', name: 'Milieu & Infrastructuur' },
  { id: 'nieuwbouw', name: 'Nieuwbouw & Verbouwing' },
  { id: 'overnames', name: 'Overnames' },
  { id: 'automatisering', name: 'Automatisering' },
];

const COMPLIANCE_TYPES = [
  { id: 'keuring', name: 'Keuring' },
  { id: 'contract', name: 'Contract' },
  { id: 'onderhoud', name: 'Onderhoud' },
  { id: 'voertuig', name: 'Voertuig' },
  { id: 'vergunning', name: 'Vergunning' },
  { id: 'betaling', name: 'Betaling' },
];

const RECURRENCE_OPTIONS = [
  { id: 'one-time', name: 'Eenmalig' },
  { id: 'monthly', name: 'Maandelijks' },
  { id: 'quarterly', name: 'Per kwartaal' },
  { id: 'half-yearly', name: 'Halfjaarlijks' },
  { id: 'yearly', name: 'Jaarlijks' },
];

const DOC_TYPE_LABELS = {
  contract: 'Contract',
  inspection_report: 'Keuringsrapport',
  invoice: 'Factuur',
  permit: 'Vergunning',
  maintenance_report: 'Onderhoudsrapport',
  construction_drawing: 'Bouwtekening',
  other: 'Overig',
};

const INPUT_CLS = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-sm text-slate-900 dark:text-[#E6EDF3] placeholder:text-slate-400 dark:placeholder:text-[#484F58] focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30 focus:border-[#2E75B6] transition-colors";

const SELECT_CLS = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-sm text-slate-900 dark:text-[#E6EDF3] focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30 focus:border-[#2E75B6] transition-colors";

const LABEL_CLS = "text-xs font-medium text-slate-700 dark:text-[#C9D1D9] block mb-1";

const CARD_CLS = "bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#30363D]";

function formatKeyDataValue(value) {
  if (value === null || value === undefined) return '\u2014';
  if (Array.isArray(value)) {
    return value.map(v => {
      if (v && typeof v === 'object') return Object.values(v).join(': ');
      return String(v);
    }).join(', ') || '\u2014';
  }
  if (typeof value === 'object') {
    return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ');
  }
  const s = String(value);
  return (s === 'null' || s === 'undefined' || s === '') ? '\u2014' : s;
}

export default function DocumentReview() {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetLocation = searchParams.get('location');
  const { authAxios } = useAuth();

  const [docData, setDocData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [keyDataOpen, setKeyDataOpen] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [locationId, setLocationId] = useState('all');
  const [tags, setTags] = useState('');
  const [content, setContent] = useState('');
  const [createCompliance, setCreateCompliance] = useState(false);
  const [compTitle, setCompTitle] = useState('');
  const [compType, setCompType] = useState('');
  const [compDueDate, setCompDueDate] = useState('');
  const [compRecurrence, setCompRecurrence] = useState('one-time');
  const [editableKeyData, setEditableKeyData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const api = authAxios();
        const [docRes, locRes] = await Promise.all([
          api.get(`/documents/${uploadId}`),
          api.get('/locations'),
        ]);
        setDocData(docRes.data);
        setLocations(locRes.data);

        const result = docRes.data.extraction_result;
        if (result) {
          setTitle(result.suggested_title || docRes.data.original_name || '');
          setCategory(result.suggested_category || '');
          setLocationId(presetLocation || result.suggested_location || 'all');
          setTags((result.suggested_tags || []).join(', '));

          if (result.suggested_compliance_item) {
            setCreateCompliance(true);
            setCompTitle(result.suggested_compliance_item.title || '');
            setCompType(result.suggested_compliance_item.type || '');
            setCompDueDate(result.suggested_compliance_item.due_date || '');
            setCompRecurrence(result.suggested_compliance_item.recurrence || 'one-time');
          }

          // Initialize editable key_data from extraction result
          if (result.key_data && typeof result.key_data === 'object') {
            setEditableKeyData(result.key_data);
          }
        } else {
          setTitle(docRes.data.original_name || '');
          if (presetLocation) setLocationId(presetLocation);
        }

        // Load preview via authenticated request
        loadPreview(api, docRes.data);
      } catch (e) {
        toast.error('Document niet gevonden');
        navigate('/kennisbank/upload');
      } finally {
        setLoading(false);
      }
    };
    load();

    return () => {
      // Cleanup blob URL on unmount
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [uploadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPreview = async (api, doc) => {
    if (!doc || (!doc.is_image && doc.mime_type !== 'application/pdf')) return;
    setPreviewLoading(true);
    try {
      const res = await api.get(`/documents/${doc.upload_id}/preview`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      setPreviewUrl(blobUrl);
    } catch (e) {
      console.error('Preview load failed:', e);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRetryExtract = async () => {
    setExtracting(true);
    try {
      const api = authAxios();
      const res = await api.post(`/documents/${uploadId}/extract`);
      if (res.data.extraction_status === 'completed' && res.data.extraction_result) {
        const result = res.data.extraction_result;
        setDocData(prev => ({ ...prev, extraction_status: 'completed', extraction_result: result }));
        setTitle(result.suggested_title || title);
        setCategory(result.suggested_category || category);
        setLocationId(presetLocation || result.suggested_location || locationId);
        setTags((result.suggested_tags || []).join(', '));
        if (result.suggested_compliance_item) {
          setCreateCompliance(true);
          setCompTitle(result.suggested_compliance_item.title || '');
          setCompType(result.suggested_compliance_item.type || '');
          setCompDueDate(result.suggested_compliance_item.due_date || '');
          setCompRecurrence(result.suggested_compliance_item.recurrence || 'one-time');
        }
        toast.success('AI extractie voltooid');
      } else {
        toast.error(res.data.error || 'Extractie mislukt');
      }
    } catch (e) {
      toast.error('Extractie mislukt');
    } finally {
      setExtracting(false);
    }
  };

  const handleConfirm = async () => {
    if (!title.trim()) { toast.error('Titel is verplicht'); return; }
    if (!category) { toast.error('Categorie is verplicht'); return; }

    setSaving(true);
    try {
      const api = authAxios();
      const payload = {
        title: title.trim(),
        category,
        location_id: locationId,
        tags,
        content,
        create_compliance: createCompliance,
        key_data: editableKeyData || undefined,
      };
      if (createCompliance) {
        payload.compliance_title = compTitle;
        payload.compliance_type = compType;
        payload.compliance_due_date = compDueDate;
        payload.compliance_recurrence = compRecurrence;
      }

      const res = await api.post(`/documents/${uploadId}/confirm`, payload);
      toast.success('Document bevestigd en opgeslagen als vault item');
      if (presetLocation) {
        navigate(`/locatie/${presetLocation}?tab=bestanden`);
      } else {
        navigate(`/kennisbank/${res.data.entry.entry_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Weet je zeker dat je dit document wilt verwijderen?')) return;
    setDeleting(true);
    try {
      const api = authAxios();
      await api.delete(`/documents/${uploadId}`);
      toast.success('Document verwijderd');
      navigate('/kennisbank');
    } catch (e) {
      toast.error('Fout bij verwijderen');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1200px]">
        <div className="h-8 w-32 bg-slate-100 dark:bg-[#21262D] rounded animate-pulse mb-4" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={`${CARD_CLS} p-6 h-96 animate-pulse`} />
          <div className={`${CARD_CLS} p-6 h-96 animate-pulse`} />
        </div>
      </div>
    );
  }

  if (!docData) return null;

  const result = docData.extraction_result;
  const isImage = docData.is_image;
  const isExcel = (docData.original_name || '').toLowerCase().endsWith('.xlsx');
  const hasFailed = docData.extraction_status === 'failed';

  return (
    <div className="max-w-[1200px]">
      <button
        onClick={() => navigate(presetLocation ? `/kennisbank/upload?location=${presetLocation}` : '/kennisbank/upload')}
        data-testid="back-to-upload"
        className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-[#8B949E] hover:text-[#2E75B6] mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Terug naar Upload
      </button>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-[#E6EDF3]">Document review</h2>
          <p className="text-xs text-slate-500 dark:text-[#8B949E] mt-0.5">{docData.original_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {result && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#2E75B6]/10 text-[#2E75B6]">
              <Sparkles className="w-3 h-3" />
              {DOC_TYPE_LABELS[result.document_type] || result.document_type}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Document preview */}
        <div className="space-y-4">
          <div className={`${CARD_CLS} overflow-hidden`} data-testid="document-preview">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-[#21262D] flex items-center gap-2">
              {isImage ? <Image className="w-4 h-4 text-emerald-500" /> : isExcel ? <Table2 className="w-4 h-4 text-emerald-500" /> : <FileText className="w-4 h-4 text-[#2E75B6]" />}
              <span className="text-xs font-medium text-slate-700 dark:text-[#C9D1D9]">{docData.original_name}</span>
              <span className="text-[10px] text-slate-400 dark:text-[#484F58] ml-auto">
                {(docData.file_size / 1024).toFixed(0)} KB
              </span>
            </div>
            <div className="p-4">
              {isImage ? (
                previewLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={docData.original_name}
                    className="w-full rounded-lg max-h-[500px] object-contain bg-slate-50 dark:bg-[#0D1117]"
                    data-testid="document-preview-image"
                  />
                ) : (
                  <div className="flex items-center justify-center h-48 text-xs text-slate-400">Preview niet beschikbaar</div>
                )
              ) : docData.mime_type === 'application/pdf' ? (
                previewLoading ? (
                  <div className="flex items-center justify-center h-[500px]">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : previewUrl ? (
                  <iframe
                    src={previewUrl}
                    title="PDF Preview"
                    className="w-full h-[500px] rounded-lg border border-slate-100 dark:border-[#30363D]"
                    data-testid="document-preview-pdf"
                  />
                ) : (
                  <div className="bg-slate-50 dark:bg-[#0D1117] rounded-lg p-4 max-h-[500px] overflow-y-auto" data-testid="document-preview-text">
                    <p className="text-xs text-slate-600 dark:text-[#8B949E] whitespace-pre-wrap">
                      {docData.extracted_text ? docData.extracted_text.substring(0, 3000) : 'Preview niet beschikbaar'}
                    </p>
                  </div>
                )
              ) : (
                <div className="bg-slate-50 dark:bg-[#0D1117] rounded-lg p-4 max-h-[500px] overflow-y-auto" data-testid="document-preview-text">
                  <p className="text-xs text-slate-600 dark:text-[#8B949E] whitespace-pre-wrap">
                    {docData.extracted_text ? docData.extracted_text.substring(0, 3000) : 'Tekst niet beschikbaar'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Key data section — editable */}
          {editableKeyData && Object.keys(editableKeyData).length > 0 && (
            <KeyDataSection
              keyData={editableKeyData}
              editable
              onChange={setEditableKeyData}
            />
          )}
        </div>

        {/* Right: Edit form */}
        <div className="space-y-4">
          {/* Status banner */}
          {hasFailed && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3" data-testid="extraction-failed-banner">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">AI extractie mislukt</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{docData.extraction_error || 'Onbekende fout'}</p>
                <button
                  onClick={handleRetryExtract}
                  disabled={extracting}
                  data-testid="retry-extract-button"
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 dark:bg-amber-800/40 text-amber-800 dark:text-amber-300 text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-800/60 transition-colors"
                >
                  {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Opnieuw proberen
                </button>
              </div>
            </div>
          )}

          {isImage && !result && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-start gap-3" data-testid="image-info-banner">
              <Image className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Afbeelding ge\u00fcpload</p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">AI beeldanalyse wordt uitgevoerd. Controleer de suggesties hieronder na extractie.</p>
              </div>
            </div>
          )}

          {/* Edit form */}
          <div className={`${CARD_CLS} p-5 space-y-4`} data-testid="document-review-form">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-[#8B949E]">Vault item details</h3>

            <div>
              <label className={LABEL_CLS}>Titel *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="review-title-input"
                className={INPUT_CLS}
                placeholder="Titel van het item"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLS}>Categorie *</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="review-category-select"
                  className={SELECT_CLS}
                >
                  <option value="">Selecteer categorie</option>
                  {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL_CLS}>Locatie</label>
                <select
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  data-testid="review-location-select"
                  className={SELECT_CLS}
                >
                  <option value="all">Alle locaties</option>
                  {locations.map(loc => <option key={loc.location_id} value={loc.location_id}>{loc.name}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={LABEL_CLS}>Tags</label>
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  data-testid="review-tags-input"
                  className={`flex-1 ${INPUT_CLS}`}
                  placeholder="tag1, tag2, tag3"
                />
              </div>
            </div>

            <div>
              <label className={LABEL_CLS}>Extra notities</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                data-testid="review-content-textarea"
                rows={3}
                className={`${INPUT_CLS} resize-none`}
                placeholder="Optionele notities bij dit document..."
              />
            </div>
          </div>

          {/* Compliance toggle */}
          <div className={`${CARD_CLS} p-5 space-y-4`} data-testid="compliance-section">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={createCompliance}
                onChange={(e) => setCreateCompliance(e.target.checked)}
                data-testid="create-compliance-toggle"
                className="w-4 h-4 rounded border-slate-300 dark:border-[#30363D] text-[#2E75B6] focus:ring-[#2E75B6]/30 dark:bg-[#0D1117]"
              />
              <div>
                <span className="text-sm font-medium text-slate-700 dark:text-[#C9D1D9]">Compliance item aanmaken</span>
                <p className="text-[10px] text-slate-400 dark:text-[#484F58]">Maak een deadline/herinnering aan op basis van dit document</p>
              </div>
              <CalendarClock className="w-4 h-4 text-slate-400 ml-auto" />
            </label>

            {createCompliance && (
              <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-[#21262D]">
                <div>
                  <label className={LABEL_CLS}>Compliance titel</label>
                  <input
                    type="text"
                    value={compTitle}
                    onChange={(e) => setCompTitle(e.target.value)}
                    data-testid="compliance-title-input"
                    className={INPUT_CLS}
                    placeholder="Bijv. Contract verlenging Rotterdam"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL_CLS}>Type</label>
                    <select
                      value={compType}
                      onChange={(e) => setCompType(e.target.value)}
                      data-testid="compliance-type-select"
                      className={SELECT_CLS}
                    >
                      <option value="">Selecteer type</option>
                      {COMPLIANCE_TYPES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Deadline</label>
                    <input
                      type="date"
                      value={compDueDate}
                      onChange={(e) => setCompDueDate(e.target.value)}
                      data-testid="compliance-due-date-input"
                      className={INPUT_CLS}
                    />
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLS}>Herhaling</label>
                  <select
                    value={compRecurrence}
                    onChange={(e) => setCompRecurrence(e.target.value)}
                    data-testid="compliance-recurrence-select"
                    className={SELECT_CLS}
                  >
                    {RECURRENCE_OPTIONS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleConfirm}
              disabled={saving}
              data-testid="confirm-document-button"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] active:scale-[0.99] transition-all disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Opslaan & Activeren
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              data-testid="delete-document-button"
              className="px-4 py-3 rounded-xl border border-slate-200 dark:border-[#30363D] text-sm font-medium text-slate-600 dark:text-[#8B949E] hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 hover:border-red-200 dark:hover:border-red-800 transition-colors"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
